import { toDate, format, parse, isValid, startOfDay } from "date-fns";
import { FieldValidator } from "./Form";
import { useFormContext } from "react-hook-form";
import { disabledInputClasses, inputClasses } from "./InputField";
import textData from "../../textData.json";
import { LangContext, LangContextType } from "../../utilities/customHooks";
import { useContext, useState, useEffect } from "react";
import { cn } from "@lib/utils";
import { Calendar } from "@components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { CalendarIcon } from "lucide-react";

export default function DatePickerWithLabel(
  {
    name,
    placeholder,
    value,
    setValue,
    disabled,
    readOnly,
    validator,
    future,
    futureDateMin,
    futureDateMax
  }
  :
  {
    name: string,
    placeholder?: string,
    value: Date | null,
    setValue: (value: Date | null) => void,
    disabled?: boolean,
    readOnly?: boolean,
    validator?: FieldValidator,
    future?: boolean,
    futureDateMin?: Date,
    futureDateMax?: Date
  }
) {
  const { langState } = useContext(LangContext) as LangContextType
  const [open, setOpen] = useState(false);

  const { register, clearErrors, setError } = useFormContext();

  const date = value ? toDate(value) : undefined;
  const [inputValue, setInputValue] = useState(date ? format(date, "MM/dd/yyyy") : "");
  // Set when the user typed a well-formed date outside [minDate, maxDate].
  // While set, the stored value is untouched and form submission is blocked
  // via the hidden input's validate below.
  const [outOfRangeMessage, setOutOfRangeMessage] = useState<string | null>(null);

  useEffect(() => {
    setInputValue(date ? format(date, "MM/dd/yyyy") : "");
  }, [value]);

  const futureMinDate = futureDateMin ? futureDateMin : new Date();
  const futureMaxDate = futureDateMax ? futureDateMax : new Date();
  const maxDate = future ? undefined : futureMaxDate;
  const minDate = future ? futureMinDate : undefined;

  // Day-granularity comparison, matching the calendar's before/after matchers —
  // a typed "today" must pass even when minDate/maxDate carry a time of day.
  const boundsError = (candidate: Date): string | null => {
    if (maxDate && startOfDay(candidate) > startOfDay(maxDate)) {
      return textData.Components.Form.DatePicker.FutureDateNotAllowed[langState];
    }
    if (minDate && startOfDay(candidate) < startOfDay(minDate)) {
      return textData.Components.Form.DatePicker.PastDateNotAllowed[langState];
    }
    return null;
  }

  const acceptDate = (accepted: Date) => {
    setValue(accepted);
    setInputValue(format(accepted, "MM/dd/yyyy"));
    setOutOfRangeMessage(null);
    clearErrors(name);
  }

  const rejectDate = (message: string) => {
    setOutOfRangeMessage(message);
    setError(name, { type: "dateRange", message });
  }

  const handleSetValue = (selectedDate: Date | undefined) => {
    if (selectedDate) {
      const rangeError = boundsError(selectedDate);
      if (rangeError) {
        rejectDate(rangeError);
      } else {
        acceptDate(selectedDate);
      }
      setOpen(false);
    }
  }

  const handleInputBlur = () => {
    if (!inputValue.trim()) {
      if (outOfRangeMessage) {
        // The rejected text was cleared — restore the stored value's display.
        setOutOfRangeMessage(null);
        clearErrors(name);
        setInputValue(date ? format(date, "MM/dd/yyyy") : "");
      }
      return;
    }
    const parsed = parse(inputValue, "MM/dd/yyyy", new Date());
    if (!isValid(parsed)) {
      // Reset to current date value if invalid
      setInputValue(date ? format(date, "MM/dd/yyyy") : "");
      return;
    }
    const rangeError = boundsError(parsed);
    if (rangeError) {
      // Keep the typed text visible so the user can see what was rejected.
      rejectDate(rangeError);
      return;
    }
    acceptDate(parsed);
  }

  const handleInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleInputBlur();
    }
  }

  const currentYear = new Date().getFullYear();
  const fromYear = future ? currentYear : currentYear - 115;
  const toYear = future ? currentYear + 20 : currentYear;

  const isDisabled = disabled || readOnly;

  const calendarDisabled = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
  ];

  return (
    <>
      {/* Hidden input for react-hook-form registration/validation */}
      <input
        type="hidden"
        {...register(name, {
          validate: () => {
            if (outOfRangeMessage) {
              return outOfRangeMessage;
            }
            if (validator?.required && !date) {
              return textData.Components.Form.CustomList.EmptyField[langState];
            }
            return true;
          }
        })}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              inputClasses,
              isDisabled && disabledInputClasses,
              "flex items-center gap-2 w-full cursor-pointer"
            )}
            onClick={() => !isDisabled && setOpen(true)}
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground shrink-0" />
            <input
              id={name}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              disabled={isDisabled}
              placeholder={placeholder || "MM/DD/YYYY"}
              className="bg-transparent border-none outline-none w-full text-sm p-0"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={handleSetValue}
            defaultMonth={date}
            captionLayout="dropdown"
            startMonth={new Date(fromYear, 0)}
            endMonth={new Date(toYear, 11)}
            disabled={calendarDisabled.length > 0 ? calendarDisabled : undefined}
          />
        </PopoverContent>
      </Popover>
    </>
  )
}
